<template>
  <div class="banner-management">
    <div class="operation-bar">
      <el-button type="primary" @click="showAddDialog">
        添加轮播图
      </el-button>
    </div>

    <!-- 移动端卡片布局 -->
    <div class="mobile-cards" v-if="isMobile">
      <el-card v-for="banner in banners" :key="banner.id" class="banner-card">
        <div class="banner-image">
          <el-image
            :src="banner.imageUrl"
            :preview-src-list="[banner.imageUrl]"
            fit="cover"
          />
        </div>
        <div class="banner-info">
          <h3>{{ banner.title }}</h3>
          <p>{{ banner.subtitle }}</p>
          <el-tag :type="banner.isEnabled ? 'success' : 'info'" size="small">
            {{ banner.isEnabled ? '启用' : '禁用' }}
          </el-tag>
        </div>
        <div class="card-actions">
          <el-button type="primary" size="small" @click="editBanner(banner)">编辑</el-button>
          <el-button type="danger" size="small" @click="deleteBanner(banner)">删除</el-button>
        </div>
      </el-card>
    </div>

    <!-- 桌面端表格布局 -->
    <el-table v-else :data="banners" style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题" />
      <el-table-column label="图片" width="200">
        <template #default="scope">
          <el-image
            :src="scope.row.imageUrl"
            :preview-src-list="[scope.row.imageUrl]"
            fit="cover"
            style="width: 150px; height: 80px"
          />
        </template>
      </el-table-column>
      <el-table-column prop="subtitle" label="副标题" />
      <el-table-column prop="link" label="链接" width="120" />
      <el-table-column prop="sortOrder" label="排序" width="80" />
      <el-table-column prop="isEnabled" label="状态" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.isEnabled ? 'success' : 'info'">
            {{ scope.row.isEnabled ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="scope">
          <el-button-group>
            <el-button type="primary" @click="editBanner(scope.row)">
              编辑
            </el-button>
            <el-button type="danger" @click="deleteBanner(scope.row)">
              删除
            </el-button>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑轮播图' : '添加轮播图'"
      width="60%"
      @close="handleDialogClose"
    >
      <el-form
        ref="formRef"
        :model="bannerForm"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="标题" prop="title">
          <el-input v-model="bannerForm.title" placeholder="请输入标题" />
        </el-form-item>

        <el-form-item label="副标题" prop="subtitle">
          <el-input v-model="bannerForm.subtitle" placeholder="请输入副标题" />
        </el-form-item>

        <el-form-item label="轮播图" prop="imageUrl">
          <image-upload 
            v-model="bannerForm.imageUrl"
            tip="建议尺寸: 1920 x 600，支持 jpg、png 格式"
          />
        </el-form-item>

        <el-form-item label="链接" prop="link">
          <el-input v-model="bannerForm.link" placeholder="例如: /products/1" />
        </el-form-item>

        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="bannerForm.sortOrder" :min="0" />
        </el-form-item>

        <el-form-item label="状态">
          <el-switch v-model="bannerForm.isEnabled" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, reactive } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import request from '@/utils/request'
import ImageUpload from '@/components/ImageUpload.vue'

export default {
  name: 'BannerManagement',
  components: {
    Plus,
    ImageUpload
  },
  setup() {
    const formRef = ref(null)
    const banners = ref([])
    const dialogVisible = ref(false)
    const isEdit = ref(false)
    const isMobile = ref(false)

    const bannerForm = reactive({
      id: null,
      title: '',
      subtitle: '',
      imageUrl: '',
      link: '',
      sortOrder: 0,
      isEnabled: true
    })

    const rules = {
      title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
      imageUrl: [{ required: true, message: '请上传图片', trigger: 'change' }]
    }

    const resetForm = () => {
      Object.assign(bannerForm, {
        id: null,
        title: '',
        subtitle: '',
        imageUrl: '',
        link: '',
        sortOrder: 0,
        isEnabled: true
      })
    }

    const handleDialogClose = () => {
      resetForm()
      if (formRef.value) {
        formRef.value.resetFields()
      }
    }

    const showAddDialog = () => {
      isEdit.value = false
      resetForm()
      dialogVisible.value = true
    }

    const editBanner = (banner) => {
      isEdit.value = true
      Object.assign(bannerForm, {
        id: banner.id,
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        imageUrl: banner.imageUrl || '',
        link: banner.link || '',
        sortOrder: banner.sortOrder || 0,
        isEnabled: banner.isEnabled ?? true
      })
      dialogVisible.value = true
    }

    const deleteBanner = async (banner) => {
      try {
        await ElMessageBox.confirm('确定要删除该轮播图吗？')
        await request.delete(`/banners/${banner.id}`)
        ElMessage.success('删除成功')
        fetchBanners()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const handleImageSuccess = (response) => {
      bannerForm.imageUrl = response.url
    }

    const beforeImageUpload = (file) => {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        ElMessage.error('只能上传图片文件!')
        return false
      }
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB!')
        return false
      }
      return true
    }

    const submitForm = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        if (isEdit.value) {
          await request.put(`/banners/${bannerForm.id}`, bannerForm)
        } else {
          await request.post('/banners', bannerForm)
        }
        
        ElMessage.success(isEdit.value ? '更新成功' : '添加成功')
        dialogVisible.value = false
        fetchBanners()
      } catch (error) {
        if (error.message) {
          ElMessage.error(error.message)
        } else {
          ElMessage.error(isEdit.value ? '更新失败' : '添加失败')
        }
      }
    }

    const fetchBanners = async () => {
      try {
        const response = await request.get('/banners')
        if (Array.isArray(response)) {
          banners.value = response
        } else {
          banners.value = []
          console.error('获取轮播图列表返回格式错误:', response)
        }
      } catch (error) {
        console.error('获取轮播图列表失败:', error)
        ElMessage.error('获取轮播图列表失败')
        banners.value = []
      }
    }

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    onMounted(() => {
      fetchBanners()
      checkMobile()
      window.addEventListener('resize', checkMobile)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
    })

    return {
      formRef,
      banners,
      dialogVisible,
      isEdit,
      bannerForm,
      rules,
      showAddDialog,
      editBanner,
      deleteBanner,
      handleDialogClose,
      submitForm,
      isMobile
    }
  }
}
</script>

<style scoped>
.banner-management {
  padding: 20px;
}

.operation-bar {
  margin-bottom: 20px;
}

.banner-image-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 360px;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.banner-image-upload:hover {
  border-color: #409eff;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-icon {
  font-size: 28px;
  color: #8c939d;
}

.image-tip {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

/* 移动端卡片样式 */
.mobile-cards {
  display: grid;
  gap: 16px;
  padding: 16px;
}

.banner-card {
  overflow: hidden;
}

.banner-image {
  margin: -20px -20px 16px;
}

.banner-image :deep(.el-image) {
  width: 100%;
  height: 150px;
}

.banner-info {
  margin-bottom: 16px;
}

.banner-info h3 {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 500;
}

.banner-info p {
  margin: 0 0 8px;
  color: #909399;
  font-size: 14px;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 768px) {
  .banner-management {
    padding: 0;
  }

  .operation-bar {
    position: sticky;
    top: 0;
    background: #fff;
    padding: 16px;
    margin: 0;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
}
</style> 